import { useCallback, useEffect, useState } from "react";

// ─────────────────── UI Components ───────────────────────────
import Modal from "../../common/Modal";
import PrimaryButton, { PrimaryButtonType } from "../../common/PrimaryButton";
import OutlinedButton from "../../common/OutlinedButton";
import Spinner from "../../common/Spinner";
import TiersInformation from "../../../pages/organizations/settings/components/TiersInformation";
import CheckCircleOutlinedIcon from "@mui/icons-material/CheckCircleOutlined";

// ─────────────────── Styles ──────────────────────────────────
import styles from "./OrganizationRequestDetailsModal.module.scss";

// ─────────────────── Store ───────────────────────────────────
import { useOrganisationStore } from "../../../providers/organisation/organisation.provider";

// ─────────────────── API ─────────────────────────────────────
import { getOrganizationDocumentations } from "../../../setup/api/services/organization";

// ─────────────────── Types ───────────────────────────────────
import { IOrganizations } from "../../../setup/types/Organisation";
import { IOrganizationDocumentation } from "../../../setup/api/services/organization/models";

// ─────────────────── Utils ───────────────────────────────────
import {
  ORGANIZATION_REQUEST_STATUS_LABELS,
  canReviewOrganizationRequest,
  getOrganizationRequestStatus,
} from "../../../setup/util/organizationRequest";

interface Props {
  showModal: boolean;
  closeModal: () => void;
  organization?: IOrganizations;
  /** Opens straight into the reject-reason step (row-level "رفض") instead of
   * the accept/reject buttons (row click / org name — "view details"). */
  initialShowRejectInput?: boolean;
  /** Immediately runs the accept flow once opened (row-level "قبول"), so the
   * fee-setup success step still appears instead of only updating the row. */
  autoAccept?: boolean;
}

const NO_DOCUMENTS_HINT =
  "لا يمكن قبول الطلب أو رفضه قبل رفع المؤسسة للمستندات المطلوبة.";

const formatRequestDate = (date?: string) =>
  date
    ? new Date(date).toLocaleDateString("ar-EG", {
        day: "numeric",
        month: "long",
        year: "numeric",
      })
    : "-";

const DocumentIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7">
    <path d="M14 2.5H7A2 2 0 0 0 5 4.5v15a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7.5l-5-5Z" />
    <path d="M14 2.5v5h5" />
    <path d="M9 12h6M9 16h4" />
  </svg>
);

const OrganizationRequestDetailsModal = ({
  showModal,
  closeModal,
  organization,
  initialShowRejectInput = false,
  autoAccept = false,
}: Props) => {
  const [showRejectInput, setShowRejectInput] = useState(
    initialShowRejectInput
  );
  const [reason, setReason] = useState("");
  // After a successful accept, the modal switches to letting the admin configure the
  // Waffy Agreement fee template/tiers right away instead of just closing — see
  // TiersInformation below. Configuring it is optional; the admin can always finish it
  // later from the organization's own Settings → "اتفاقية وفّي" tab.
  const [showFeeSetup, setShowFeeSetup] = useState(false);

  // Supporting documents the organization uploaded with its request (IBAN letter,
  // national address, commercial register, …). Same source as the organization's
  // Settings → Documentation tab, so whatever the admin sees there shows up here.
  const [documents, setDocuments] = useState<IOrganizationDocumentation[]>([]);
  const [documentsLoading, setDocumentsLoading] = useState(false);

  const buttonLoading = useOrganisationStore((state) => state.buttonLoading);
  const verifyOrganisation = useOrganisationStore(
    (state) => state.verifyOrganisation
  );
  const rejectOrganisation = useOrganisationStore(
    (state) => state.rejectOrganisation
  );

  // Reset the reject-reason step whenever a different org is opened / the modal closes,
  // respecting the caller's requested initial step (row-level Reject vs. view details).
  useEffect(() => {
    setShowRejectInput(initialShowRejectInput);
    setReason("");
    setShowFeeSetup(false);
  }, [organization?.code, showModal, initialShowRejectInput]);

  useEffect(() => {
    const code = organization?.code;
    if (!showModal || !code) {
      setDocuments([]);
      return;
    }

    let cancelled = false;
    setDocumentsLoading(true);
    getOrganizationDocumentations(code)
      .then((response) => {
        if (!cancelled) setDocuments(response.data || []);
      })
      .catch((error) => {
        // A failed fetch is treated as "no documents", which also keeps the
        // accept/reject actions disabled — better than approving blind.
        console.error("Error fetching organization request documents:", error);
        if (!cancelled) setDocuments([]);
      })
      .finally(() => {
        if (!cancelled) setDocumentsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [showModal, organization?.code]);

  const handleClose = () => {
    closeModal();
  };

  const handleAccept = useCallback(async () => {
    if (!organization?.code) return;
    const accepted = await verifyOrganisation(organization.code);
    if (accepted) {
      setShowFeeSetup(true);
    }
    // On failure, verifyOrganisation already surfaced a toast — keep the modal open on
    // the accept/reject step so the admin can retry instead of silently closing it.
  }, [organization?.code, verifyOrganisation]);

  // Row-level "قبول" opens the modal with autoAccept instead of calling verifyOrganisation
  // directly, so this runs the exact same accept flow (and fee-setup success step) as
  // clicking "قبول وتفعيل المؤسسة" from inside the modal.
  useEffect(() => {
    if (!showModal || !autoAccept || !organization?.code) return;
    handleAccept();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showModal, autoAccept, organization?.code]);

  const handleConfirmReject = async () => {
    if (!organization?.code || !reason.trim()) return;
    await rejectOrganisation(organization.code, reason.trim());
    handleClose();
  };

  const openDocument = (url?: string) => {
    if (url) window.open(url, "_blank", "noopener,noreferrer");
  };

  const isPending = organization?.verificationStatus === "PENDING";
  const isRejected = organization?.verificationStatus === "REJECTED";
  const admin = organization?.organizationAdmin;

  // A request can't be judged before its documents are in. While they're still
  // loading we also hold the actions, so the admin never acts on an unknown state.
  const hasDocuments = documents.length > 0;
  const canReview =
    canReviewOrganizationRequest(organization, hasDocuments) &&
    !documentsLoading;
  const status = getOrganizationRequestStatus(organization, hasDocuments);

  return (
    <Modal
      size={showFeeSetup ? "large" : undefined}
      customModalClassName={
        showFeeSetup ? styles.feeSetupModal : styles.requestModal
      }
      visible={showModal}
      onCancel={handleClose}>
      {showFeeSetup ? (
        <div className={`${styles.container} ${styles.feeSetupContainer}`}>
          <div className={styles.feeSetupHeader}>
            <div className={styles.successBanner}>
              <CheckCircleOutlinedIcon />
              <span>تم قبول طلب {organization?.name} بنجاح</span>
            </div>
            <p>
              يمكنك تحديد رسوم اتفاقية وفّي الآن، أو لاحقًا من صفحة إعدادات
              المؤسسة.
            </p>
          </div>
          <TiersInformation
            organization={organization}
            isEditable
            secondaryAction={
              <OutlinedButton onClick={handleClose}>إغلاق</OutlinedButton>
            }
          />
        </div>
      ) : (
        <div className={styles.container}>
          <div className={styles.head}>
            <h3>طلب تفعيل مؤسسة</h3>
            <span>
              رقم الطلب{" "}
              <span className={styles.ltr}>#{organization?.code}</span> ·{" "}
              {formatRequestDate(organization?.createdAt)}
            </span>
            {/* Held back until the documents resolve — the label depends on them,
                and flashing "قيد المراجعة" first would be misleading. */}
            {!documentsLoading && (
              <div>
                <span
                  className={`${styles.chip} ${
                    styles[`chip_${status.toLowerCase()}`]
                  }`}>
                  {ORGANIZATION_REQUEST_STATUS_LABELS[status]}
                </span>
              </div>
            )}
          </div>

          <div className={styles.section}>
            <h4>بيانات مقدم الطلب</h4>
            <div className={styles.grid}>
              <div className={styles.item}>
                <b>الاسم</b>
                <span>{admin?.profile?.fullName || "-"}</span>
              </div>
              <div className={styles.item}>
                <b>رقم الجوال</b>
                <span className={styles.ltr}>{admin?.phoneNumber || "-"}</span>
              </div>
              <div className={styles.item}>
                <b>البريد الإلكتروني</b>
                <span className={styles.ltr}>{admin?.email || "-"}</span>
              </div>
            </div>
          </div>

          <div className={styles.section}>
            <h4>بيانات المؤسسة</h4>
            <div className={styles.grid}>
              <div className={styles.item}>
                <b>اسم المؤسسة</b>
                <span className={styles.orgName}>
                  {organization?.logoUrl && (
                    <img
                      className={styles.logo}
                      src={organization.logoUrl}
                      alt={organization.name}
                    />
                  )}
                  {organization?.name || "-"}
                </span>
              </div>
              <div className={styles.item}>
                <b>رابط الموقع الإلكتروني</b>
                {organization?.websiteUrl ? (
                  <a
                    className={`${styles.link} ${styles.ltr}`}
                    href={organization.websiteUrl}
                    target="_blank"
                    rel="noreferrer">
                    {organization.websiteUrl}
                  </a>
                ) : (
                  <span>-</span>
                )}
              </div>
            </div>
            <div className={`${styles.item} ${styles.descriptionItem}`}>
              <b>وصف المؤسسة</b>
              <span>{organization?.description || "-"}</span>
            </div>
          </div>

          <div className={styles.section}>
            <h4>المستندات</h4>
            {documentsLoading ? (
              <div className={styles.documentsLoading}>
                <Spinner />
              </div>
            ) : documents.length === 0 ? (
              <p className={styles.noDocuments}>لا توجد مستندات مرفقة</p>
            ) : (
              documents.map((doc) => (
                <div key={doc.id} className={styles.doc}>
                  <span className={styles.docIcon}>
                    <DocumentIcon />
                  </span>
                  <span className={styles.docText}>
                    <b>{doc.title}</b>
                    <span>{doc.description || doc.attachmentUrl}</span>
                  </span>
                  <button
                    className={styles.docView}
                    type="button"
                    disabled={!doc.attachmentUrl}
                    onClick={() => openDocument(doc.attachmentUrl)}>
                    عرض
                  </button>
                </div>
              ))
            )}
          </div>

          {isRejected && (
            <div className={styles.rejectedBox}>
              <strong>سبب الرفض</strong>
              <p>{organization?.rejectionReason || "-"}</p>
            </div>
          )}

          {isPending && showRejectInput && (
            <div className={styles.rejectPanel}>
              <label htmlFor="organization-reject-reason">سبب الرفض</label>
              <textarea
                id="organization-reject-reason"
                rows={4}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="اكتب سببًا واضحًا يساعد المستخدم على تصحيح بياناته"
                autoFocus
              />
              <div className={styles.actions}>
                <PrimaryButton
                  buttonType={PrimaryButtonType.DANGER}
                  disabled={buttonLoading || !reason.trim() || !canReview}
                  onClick={handleConfirmReject}>
                  {buttonLoading ? <Spinner /> : "تأكيد الرفض"}
                </PrimaryButton>
                <OutlinedButton
                  disabled={buttonLoading}
                  onClick={() => setShowRejectInput(false)}>
                  إلغاء
                </OutlinedButton>
              </div>
            </div>
          )}

          {isPending && !showRejectInput && (
            <>
              {!documentsLoading && !hasDocuments && (
                <p className={styles.noDocumentsHint}>{NO_DOCUMENTS_HINT}</p>
              )}
              <div className={styles.actions}>
                <PrimaryButton
                  style={{ flex: 1.4, width: "100%" }}
                  onClick={handleAccept}
                  disabled={buttonLoading || !canReview}>
                  {buttonLoading ? <Spinner /> : "قبول وتفعيل المؤسسة"}
                </PrimaryButton>
                <button
                  className={styles.dangerOutlineBtn}
                  type="button"
                  disabled={buttonLoading || !canReview}
                  onClick={() => setShowRejectInput(true)}>
                  رفض الطلب
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </Modal>
  );
};

export default OrganizationRequestDetailsModal;
